import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/switch';
import { Brain, Trash2, Plus, AlertTriangle } from 'lucide-react';
import { apiFetch } from '@/utils/api';

interface LocalCategoryModel {
    id: string;
    version: number;
    is_active: boolean;
    training_examples_count: number;
    categories_count: number;
    vendors_count: number;
    created_at: string;
    model_path: string;
}

interface LocalCategoryOverride {
    id: string;
    product_pattern: string;
    vendor_pattern?: string;
    category_id: string;
    category_name: string;
    category_type: 'cost' | 'revenue';
    override_type: 'hard' | 'soft';
    confidence_boost: number;
    is_active: boolean;
    created_at: string;
}

interface Category {
    id: string;
    name: string;
}

export default function LocalCategoryModelsPage() {
    // Removed unused router and t imports

    const [models, setModels] = useState<LocalCategoryModel[]>([]);
    const [overrides, setOverrides] = useState<LocalCategoryOverride[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [revenueCategories, setRevenueCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isTraining, setIsTraining] = useState(false);
    // Removed dialog-based override form - using inline editing instead

    // Tab state management
    const [activeTab, setActiveTab] = useState('models');

    // Inline editing state
    const [newOverride, setNewOverride] = useState({
        product_pattern: '',
        vendor_pattern: '',
        category_type: 'cost' as 'cost' | 'revenue',
        category_id: '',
        override_type: 'soft' as 'hard' | 'soft',
        confidence_boost: 0.2,
        is_active: true
    });
    const [isAddingInline, setIsAddingInline] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);

            const [modelsRes, overridesRes, categoriesRes, revenueCategoriesRes] = await Promise.all([
                apiFetch('/api/local-category-models/models').then(res => res.json()),
                apiFetch('/api/local-category-models/overrides').then(res => res.json()),
                apiFetch('/api/categories/').then(res => res.json()),
                apiFetch('/api/revenue-categories/').then(res => res.json())
            ]);

            // Ensure we always have arrays, even if API returns something else
            const processedModels = Array.isArray(modelsRes) ? modelsRes : [];
            const processedOverrides = Array.isArray(overridesRes) ? overridesRes : [];
            const processedCategories = Array.isArray(categoriesRes) ? categoriesRes.filter((cat: any) => cat.parent_id !== null) : [];
            const processedRevenueCategories = Array.isArray(revenueCategoriesRes) ? revenueCategoriesRes.filter((cat: any) => cat.parent_id !== null) : [];

            setModels(processedModels);
            setOverrides(processedOverrides);
            setCategories(processedCategories);
            setRevenueCategories(processedRevenueCategories);
        } catch (err: any) {
            console.error('Error loading data:', err);
            setError(err.message || 'Failed to load data');
            // Set empty arrays on error
            setModels([]);
            setOverrides([]);
            setCategories([]);
            setRevenueCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const trainModel = async () => {
        try {
            setIsTraining(true);
            setError(null);

            const response: any = await apiFetch('/api/local-category-models/train', {
                method: 'POST',
                body: JSON.stringify({
                    min_examples_per_category: 3,
                    max_examples_per_category: null
                })
            }).then(res => res.json());

            if (response.message) {
                await loadData();
                alert(response.message);
            } else {
                throw new Error('Training failed');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to start training');
        } finally {
            setIsTraining(false);
        }
    };

    const deployModel = async (version: number) => {
        try {
            const response: any = await apiFetch(`/api/local-category-models/models/${version}/deploy`, {
                method: 'POST'
            }).then(res => res.json());

            if (response.message) {
                await loadData();
                alert(response.message);
            } else {
                throw new Error('Deployment failed');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to deploy model');
        }
    };

    const deleteOverride = async (id: string) => {
        if (!confirm('Are you sure you want to delete this override?')) return;

        try {
            await apiFetch(`/api/local-category-models/overrides/${id}`, {
                method: 'DELETE'
            }).then(res => res.json());
            await loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to delete override');
        }
    };

    const resetNewOverride = () => {
        setNewOverride({
            product_pattern: '',
            vendor_pattern: '',
            category_type: 'cost',
            category_id: '',
            override_type: 'soft',
            confidence_boost: 0.2,
            is_active: true
        });
    };

    const saveInlineOverride = async () => {
        if (!newOverride.product_pattern || !newOverride.category_id) {
            setError('Product pattern and category are required');
            return;
        }

        try {
            const response: any = await apiFetch('/api/local-category-models/overrides', {
                method: 'POST',
                body: JSON.stringify(newOverride)
            }).then(res => res.json());

            if (response.id) {
                await loadData();
                resetNewOverride();
                setIsAddingInline(false);
                // Ensure we stay on the overrides tab
                setActiveTab('overrides');
            } else {
                throw new Error('Failed to save override');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to save override');
        }
    };

    const cancelInlineAdd = () => {
        resetNewOverride();
        setIsAddingInline(false);
    };

    const availableCategories = newOverride.category_type === 'revenue' ? revenueCategories : categories;

    if (loading) {
        return (
            <Layout>
                <div className="container mx-auto px-4 py-8">
                    <div className="space-y-4">
                        <Skeleton className="h-8 w-64" />
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-64 w-full" />
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                        <Brain className="h-8 w-8" />
                        Automatic categorization
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        Manage organization-specific ML models for automatic product categorization
                    </p>
                </div>

                {error && (
                    <Alert className="mb-6">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList>
                        <TabsTrigger value="models">Models</TabsTrigger>
                        <TabsTrigger value="overrides">Overrides</TabsTrigger>
                    </TabsList>

                    <TabsContent value="models">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle>Model Versions</CardTitle>
                                        <CardDescription>
                                            Trained models for your organization. The active model is used for category suggestions.
                                        </CardDescription>
                                    </div>
                                    <Button onClick={trainModel} disabled={isTraining}>
                                        <Brain className="h-4 w-4 mr-2" />
                                        {isTraining ? 'Training...' : 'Train New Model'}
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Version</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Training Data</TableHead>
                                            <TableHead>Categories</TableHead>
                                            <TableHead>Vendors</TableHead>
                                            <TableHead>Created</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(models || []).map((model) => (
                                            <TableRow key={model.id}>
                                                <TableCell className="font-medium">v{model.version}</TableCell>
                                                <TableCell>
                                                    {model.is_active ? (
                                                        <Badge variant="default">Active</Badge>
                                                    ) : (
                                                        <Badge variant="secondary">Inactive</Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>{model.training_examples_count} examples</TableCell>
                                                <TableCell>{model.categories_count}</TableCell>
                                                <TableCell>{model.vendors_count}</TableCell>
                                                <TableCell>
                                                    {new Date(model.created_at).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell>
                                                    {!model.is_active && (
                                                        <Button
                                                            size="sm"
                                                            onClick={() => deployModel(model.version)}
                                                        >
                                                            Deploy
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="overrides">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle>Category Overrides</CardTitle>
                                        <CardDescription>
                                            Define rules to override or boost category suggestions for specific products or vendors.
                                        </CardDescription>
                                    </div>
                                    <Button
                                        onClick={() => {
                                            setActiveTab('overrides');
                                            setIsAddingInline(true);
                                        }}
                                        disabled={isAddingInline}
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add Override
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Product Pattern</TableHead>
                                            <TableHead>Vendor Pattern</TableHead>
                                            <TableHead>Target</TableHead>
                                            <TableHead>Category</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Boost</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {/* Inline add row */}
                                        {isAddingInline && (
                                            <TableRow className="bg-muted/50">
                                                <TableCell>
                                                    <Input
                                                        placeholder="e.g., *coffee*, espresso"
                                                        value={newOverride.product_pattern}
                                                        onChange={(e) => setNewOverride({ ...newOverride, product_pattern: e.target.value })}
                                                        className="h-8"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        placeholder="Optional vendor"
                                                        value={newOverride.vendor_pattern}
                                                        onChange={(e) => setNewOverride({ ...newOverride, vendor_pattern: e.target.value })}
                                                        className="h-8"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={newOverride.category_type}
                                                        onValueChange={(value: 'cost' | 'revenue') => setNewOverride({
                                                            ...newOverride,
                                                            category_type: value,
                                                            category_id: ''
                                                        })}
                                                    >
                                                        <SelectTrigger className="h-8">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="cost">Cost</SelectItem>
                                                            <SelectItem value="revenue">Revenue</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={newOverride.category_id}
                                                        onValueChange={(value) => setNewOverride({ ...newOverride, category_id: value })}
                                                    >
                                                        <SelectTrigger className="h-8">
                                                            <SelectValue placeholder="Select category" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {(availableCategories || []).map((category) => (
                                                                <SelectItem key={category.id} value={category.id}>
                                                                    {category.name}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={newOverride.override_type}
                                                        onValueChange={(value: 'hard' | 'soft') => setNewOverride({ ...newOverride, override_type: value })}
                                                    >
                                                        <SelectTrigger className="h-8">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="soft">Soft</SelectItem>
                                                            <SelectItem value="hard">Hard</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    {newOverride.override_type === 'soft' ? (
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            step="5"
                                                            value={(newOverride.confidence_boost * 100).toString()}
                                                            onChange={(e) => setNewOverride({ ...newOverride, confidence_boost: parseFloat(e.target.value) / 100 })}
                                                            className="h-8 w-16"
                                                        />
                                                    ) : (
                                                        <span className="text-muted-foreground">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Switch
                                                        checked={newOverride.is_active}
                                                        onChange={(checked: boolean) => setNewOverride({ ...newOverride, is_active: checked })}
                                                        className=""
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <Button
                                                            size="sm"
                                                            onClick={saveInlineOverride}
                                                            className="h-8 px-2"
                                                        >
                                                            Save
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={cancelInlineAdd}
                                                            className="h-8 px-2"
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        )}

                                        {/* Existing overrides */}
                                        {(overrides || []).map((override) => (
                                            <TableRow key={override.id}>
                                                <TableCell className="font-medium">{override.product_pattern}</TableCell>
                                                <TableCell>{override.vendor_pattern || '-'}</TableCell>
                                                <TableCell>
                                                    <Badge variant={override.category_type === 'revenue' ? 'secondary' : 'default'}>
                                                        {override.category_type === 'revenue' ? 'Revenue' : 'Cost'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>{override.category_name}</TableCell>
                                                <TableCell>
                                                    <Badge variant={override.override_type === 'hard' ? 'destructive' : 'default'}>
                                                        {override.override_type}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>+{(override.confidence_boost * 100).toFixed(0)}%</TableCell>
                                                <TableCell>
                                                    {override.is_active ? (
                                                        <Badge variant="default">Active</Badge>
                                                    ) : (
                                                        <Badge variant="secondary">Inactive</Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => deleteOverride(override.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                {/* Dialog removed - using inline editing instead */}
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/admin/local-category-models')({
    component: LocalCategoryModelsPage,
})
